"use client";

import type { UserData } from "@/features/auth/store/useUserStore.ts";

import * as React from "react";
import { FileText, LogOut, Scale, Settings, Shield, User } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import PATHS from "@/routes/paths.ts";
import { cn } from "@/lib/utils.ts";
import LanguageSwitcher from "@/layouts/AuthLayout/components/LanguageSwitcher/LanguageSwitcher.tsx";

interface SidebarUserDropdownProps {
  user: UserData | null;
  isExpanded: boolean;
}

export function SidebarUserDropdown({ user, isExpanded }: SidebarUserDropdownProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const redirect = useNavigate();
  const { t } = useTranslation(["sidebar", "common"]);

  // Animation constants
  const ANIMATION_DURATION = 0.3;
  const ANIMATION_EASE = [0.25, 0.1, 0.25, 1.0];

  return (
    <DropdownMenu modal={true} open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          className={cn(
            "hover:bg-primary/30 data-[state=open]:bg-primary/30 w-full flex-row items-center justify-start rounded-none focus-visible:ring-0 focus-visible:ring-offset-0",
            !isExpanded ? "p-0!" : "absolute bottom-0 left-0 h-fit pb-5",
          )}
          variant="ghost"
        >
          <motion.div
            layout
            className={cn("flex items-center gap-2")}
            transition={{ duration: ANIMATION_DURATION, ease: ANIMATION_EASE }}
          >
            <Avatar className="size-6">
              <AvatarImage
                alt={user?.firstName as string}
                className={"h-full w-full object-contain"}
                src={(user?.avatarUrl as string) || "/placeholder.svg"}
              />
              <AvatarFallback>{user?.firstName ? user.firstName.charAt(0) : "U"}</AvatarFallback>
            </Avatar>

            <AnimatePresence>
              {isExpanded && (
                <motion.div
                  layout
                  animate={{ width: "auto", opacity: 1 }}
                  className="flex flex-col overflow-hidden text-start"
                  exit={{ width: 0, opacity: 0 }}
                  initial={{ width: 0, opacity: 0 }}
                  transition={{
                    duration: ANIMATION_DURATION,
                    ease: ANIMATION_EASE,
                    opacity: { duration: ANIMATION_DURATION * 0.7 },
                  }}
                >
                  <span className="max-w-[120px] truncate text-xs font-medium">
                    {user?.fullName}
                  </span>
                  <span className="text-muted-foreground truncate text-xs capitalize">
                    {t(`common:roles.${user?.role ?? "student"}`)}
                  </span>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </Button>
      </DropdownMenuTrigger>

      <AnimatePresence>
        {isOpen && (
          <DropdownMenuContent asChild forceMount align="start" className="z-[1502]! w-56">
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              initial={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2, ease: ANIMATION_EASE }}
            >
              <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                <LanguageSwitcher
                  align={"start"}
                  btnClassName={"w-full justify-start bg-transparent border-none"}
                  containerClassName={"w-full z-[1503]"}
                  imgClassName={"size-8"}
                  side={"top"}
                />
              </DropdownMenuItem>
              <DropdownMenuItem
                className="focus:bg-accent focus:text-accent-foreground px-4 py-2"
                onSelect={() => redirect(PATHS.TUTOR.PUBLIC_PROFILE)}
              >
                <motion.div
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-center gap-2"
                  initial={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2, delay: 0.1, ease: ANIMATION_EASE }}
                >
                  <User className="h-4 w-4" />
                  <span>{t("sidebar:tutor.public_profile")}</span>
                </motion.div>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="focus:bg-accent focus:text-accent-foreground px-4 py-2"
                onSelect={() => redirect("/")}
              >
                <motion.div
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-center gap-2"
                  initial={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2, delay: 0.15, ease: ANIMATION_EASE }}
                >
                  <Settings className="h-4 w-4" />
                  <span>{t("common:settings")}</span>
                </motion.div>
              </DropdownMenuItem>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="focus:bg-accent focus:text-accent-foreground px-4 py-2">
                  <motion.div
                    animate={{ opacity: 1, x: 0 }}
                    className="flex items-center gap-2"
                    initial={{ opacity: 0, x: -10 }}
                    transition={{ duration: 0.2, delay: 0.175, ease: ANIMATION_EASE }}
                  >
                    <Scale className="h-4 w-4" />
                    {/* @ts-ignore */}
                    <span>{t("sidebar:legal")}</span>
                  </motion.div>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="z-[1504]!">
                  <DropdownMenuItem
                    className="focus:bg-accent focus:text-accent-foreground cursor-pointer px-4 py-2"
                    onSelect={() => redirect(PATHS.TERMS_OF_SERVICE)}
                  >
                    <FileText className="mr-2 h-4 w-4" />
                    {/* @ts-ignore */}
                    <span>{t("sidebar:terms_of_service")}</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="focus:bg-accent focus:text-accent-foreground cursor-pointer px-4 py-2"
                    onSelect={() => redirect(PATHS.PRIVACY_POLICY)}
                  >
                    <Shield className="mr-2 h-4 w-4" />
                    {/* @ts-ignore */}
                    <span>{t("sidebar:privacy_policy")}</span>
                  </DropdownMenuItem>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="focus:bg-accent focus:text-accent-foreground px-4 py-2"
                onSelect={() => redirect(PATHS.LOG_OUT)}
              >
                <motion.div
                  animate={{ opacity: 1, x: 0 }}
                  className="flex items-center gap-2"
                  initial={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2, delay: 0.2, ease: ANIMATION_EASE }}
                >
                  <LogOut className="h-4 w-4" />
                  <span>{t("common:logout")}</span>
                </motion.div>
              </DropdownMenuItem>
            </motion.div>
          </DropdownMenuContent>
        )}
      </AnimatePresence>
    </DropdownMenu>
  );
}
